import {
  fileTypes as defaultFileTypes,
  MAX_UPLOAD_SIZE_BYTES,
} from '@porsche-code/kado-file-upload';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  Dropzone,
  DropzoneFiles,
  DropzoneInner,
  FileRejection,
  Label,
  useDropzone,
} from '@porsche-code/kado-ui';
import { PInlineNotification } from '@porsche-design-system/components-react';
import get from 'lodash/get';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { UploadProgress } from '@/lib/uploader';

const Error = styled(PInlineNotification, {
  base: {
    marginBottom: 'sm',
  },
});

export const FileUploader = ({
  label,
  value,
  onChange,
  progress,
  fileTypes = defaultFileTypes,
  multiple = true,
}: {
  label?: string;
  value: {
    attachments: {
      id: string;
      name: string;
      size: number;
      type: string;
    }[];
    attachmentsToUpload: {
      id: string;
      file: File;
    }[];
  };
  onChange: (value: {
    attachments: {
      id: string;
      name: string;
      size: number;
      type: string;
    }[];
    attachmentsToUpload: {
      id: string;
      file: File;
    }[];
  }) => void;
  progress?: UploadProgress;
  fileTypes?: typeof defaultFileTypes;
  multiple?: boolean;
}) => {
  const { t } = useTranslation(NAMESPACES);

  const [errors, setErrors] = useState<string[] | undefined>();

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      onChange({
        ...value,
        attachmentsToUpload: [
          ...value.attachmentsToUpload,
          ...acceptedFiles.map((acceptedFile) => ({
            id: crypto.randomUUID(),
            file: acceptedFile,
          })),
        ],
      });
    },
    [onChange, value],
  );

  const onDropRejected = useCallback((files: FileRejection[]) => {
    const errorMessages = files.flatMap((file) =>
      file.errors.flatMap((error) => error.code),
    );
    setErrors([...new Set(errorMessages)]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    useFsAccessApi: false,
    maxSize: MAX_UPLOAD_SIZE_BYTES,
    multiple,
    accept: fileTypes.reduce(
      (memo, { type, extension }) => ({ ...memo, [type]: [`.${extension}`] }),
      {},
    ),
  });

  return (
    <>
      {errors?.map((error) => {
        const isInvalidTypeError = error === 'file-invalid-type';
        const isFileTooLargeError = error === 'file-too-large';

        return (
          <Error
            key={error}
            state="error"
            heading={
              isInvalidTypeError
                ? t('common:upload.typeError.title')
                : isFileTooLargeError
                  ? t('common:upload.sizeError.title')
                  : t('common:error')
            }
            onDismiss={() => setErrors(undefined)}
          >
            {isInvalidTypeError
              ? t('common:upload.typeError.description', {
                  allowedTypes: fileTypes
                    .map(({ extension }) => extension)
                    .join(', '),
                })
              : isFileTooLargeError
                ? t('common:upload.sizeError.description')
                : error}
          </Error>
        );
      })}
      {label && <Label label={label} />}
      <Dropzone {...getRootProps({ role: 'button', 'aria-label': label })}>
        <input {...getInputProps()} />
        <DropzoneInner
          isDragActive={isDragActive}
          i18n={{
            description: t('common:upload.description'),
            dragDescription: t('common:upload.dragDescription'),
            allowedTypes: t('common:upload.allowedTypes', {
              allowedTypes: fileTypes
                .map(({ extension }) => `.${extension}`)
                .join(', '),
            }),
          }}
        />
      </Dropzone>
      <DropzoneFiles
        progress={value.attachmentsToUpload.map((file) =>
          get(progress, file.id, -1),
        )}
        existingFiles={value.attachments}
        files={value.attachmentsToUpload}
        onDelete={(index) => {
          onChange({
            ...value,
            attachmentsToUpload: value.attachmentsToUpload.filter(
              (_, i) => i !== index,
            ),
          });
        }}
        onDeleteExisting={(idToDelete) => {
          onChange({
            ...value,
            attachments: value.attachments.filter(
              ({ id }) => id !== idToDelete,
            ),
          });
        }}
      />
    </>
  );
};
